import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { microskillApi, videoApi, skillstreeApi } from '../services/api';
import { subscriptionService } from '../services/subscriptionService';
import { dashboardService } from '../services/dashboardService';
import { modalApi } from '../services/modalApi';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { useUser } from '../contexts/UserContext';
import './MicroskillDetailPage.css';

interface MicrolearningModule {
  id: number;
  title: string;
  duration: number;
  completed: boolean;
  current: boolean;
  videoId: number; // Store video_id for navigation
  thumbnail?: string; // Thumbnail storage_path from API
}

interface NextMicroskill {
  id: number;
  code: string;
  title: string;
  duration: number;
  instructors: string;
  microlearningCount: number;
  imageUrl?: string; // Image URL from micro_skills
  isFree?: boolean; // Store is_free status from API
  canAccess?: boolean; // Store can_access status from API
  subModules: {
    id: number;
    title: string;
    trainer: string;
    views: number;
    thumbnail?: string; // Thumbnail storage_path from API
  }[];
}

interface MicroskillDetail {
  id: number;
  code: string;
  name: string;
  title: string;
  description: string;
  duration: number;
  totalViewers: number;
  trainer: string;
  skillTag: string;
  sessionCount: number;
  learningProgress: number;
  learningStatus?: string; // Store learning_status from API (e.g., 'completed', 'in_progress')
  imageUrl?: string;
  courseProgress: {
    completed: number;
    total: number;
    remaining: number;
  };
  microlearningModules: MicrolearningModule[];
  nextMicroskill: NextMicroskill;
}

// Helper function to check if achievement modal has been shown for a microskill
// Uses API, falls back to localStorage read-only for backward compatibility if API fails
const hasShownAchievementModalForMicroskill = async (microskillId: number, userId?: number): Promise<boolean> => {
  // If no userId (not authenticated), return false (can't check database)
  if (!userId) {
    return false;
  }

  // Try API
  try {
    const response = await modalApi.checkModal('achievement', microskillId);
    return response.has_shown;
  } catch (error) {
    console.error('Error checking achievement modal from API, falling back to localStorage read:', error);
    // Fallback to localStorage read-only for backward compatibility
    try {
      const key = `achievement_modal_shown_${userId}_${microskillId}`;
      return localStorage.getItem(key) === 'true';
    } catch (localError) {
      console.error('Error reading from localStorage:', localError);
      return false;
    }
  }
};

// Helper function to mark achievement modal as shown for a microskill
const markAchievementModalAsShown = async (microskillId: number, userId?: number): Promise<void> => {
  // Only store in database if user is authenticated
  if (userId) {
    try {
      await modalApi.markModalAsShown('achievement', microskillId);
    } catch (error) {
      console.error('Error marking achievement modal as shown in API:', error);
    }
  }
};

// Helper function to check if skillstree completion modal has been shown for a skillstree
const hasShownSkillstreeCompletionModalForSkillstree = async (skillstreeId: number, userId?: number): Promise<boolean> => {
  // If no userId (not authenticated), return false (can't check database)
  if (!userId) {
    return false;
  }

  // Try API
  try {
    const response = await modalApi.checkModal('skillstree_completion', skillstreeId);
    return response.has_shown;
  } catch (error) {
    console.error('Error checking skillstree completion modal from API, falling back to localStorage read:', error);
    // Fallback to localStorage read-only for backward compatibility
    try {
      const key = `skillstree_completion_modal_shown_${userId}_${skillstreeId}`;
      return localStorage.getItem(key) === 'true';
    } catch (localError) {
      console.error('Error reading from localStorage:', localError);
      return false;
    }
  }
};

// Helper function to mark skillstree completion modal as shown for a skillstree
const markSkillstreeCompletionModalAsShown = async (skillstreeId: number, userId?: number): Promise<void> => {
  // Only store in database if user is authenticated
  if (userId) {
    try {
      await modalApi.markModalAsShown('skillstree_completion', skillstreeId);
    } catch (error) {
      console.error('Error marking skillstree completion modal as shown in API:', error);
    }
  }
};

// Helper function to check if passport modal has been shown for a skillstree
const hasShownPassportModalForSkillstree = async (skillstreeId: number, userId?: number): Promise<boolean> => {
  // If no userId (not authenticated), return false (can't check database)
  if (!userId) {
    return false;
  }

  // Try API
  try {
    const response = await modalApi.checkModal('passport', skillstreeId);
    return response.has_shown;
  } catch (error) {
    console.error('Error checking passport modal from API, falling back to localStorage read:', error);
    // Fallback to localStorage read-only for backward compatibility
    try {
      const key = `passport_modal_shown_${userId}_${skillstreeId}`;
      return localStorage.getItem(key) === 'true';
    } catch (localError) {
      console.error('Error reading from localStorage:', localError);
      return false;
    }
  }
};

// Helper function to mark passport modal as shown for a skillstree
const markPassportModalAsShown = async (skillstreeId: number, userId?: number): Promise<void> => {
  // Only store in database if user is authenticated
  if (userId) {
    try {
      await modalApi.markModalAsShown('passport', skillstreeId);
    } catch (error) {
      console.error('Error marking passport modal as shown in API:', error);
    }
  }
};

const MicroskillDetailPage = () => {
  const { categoryId, skillstreeId, microskillId } = useParams<{ 
    categoryId: string; 
    skillstreeId: string; 
    microskillId: string 
  }>();
  const navigate = useNavigate();
  const { user } = useUser();
  
  const [microskill, setMicroskill] = useState<MicroskillDetail | null>(null);
  const [categoryTitle, setCategoryTitle] = useState<string>('');
  const [skillstreeName, setSkillstreeName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [videoData, setVideoData] = useState<any>(null);
  const [videoLoading, setVideoLoading] = useState<boolean>(false);
  const [videoError, setVideoError] = useState<boolean>(false);
  const [videoErrorDetails, setVideoErrorDetails] = useState<{ type: 'CORS' | 'NETWORK' | 'UNKNOWN'; message: string; url?: string } | null>(null);
  const [videoProgress, setVideoProgress] = useState<{ completion_rate: number; isCompleted: boolean } | null>(null);
  const [videosList, setVideosList] = useState<any[]>([]); // Store the list of videos for navigation
  const [courseProgressExpanded, setCourseProgressExpanded] = useState<boolean>(true);
  const [nextMicroskillExpanded, setNextMicroskillExpanded] = useState<boolean>(true);
  const [submodulesExpanded, setSubmodulesExpanded] = useState<boolean>(true);
  const [showAchievementModal, setShowAchievementModal] = useState<boolean>(false);
  const [hasShownAchievementModal, setHasShownAchievementModal] = useState<boolean>(false);
  const [showSkillstreeCompletionModal, setShowSkillstreeCompletionModal] = useState<boolean>(false);
  const [hasShownSkillstreeCompletionModal, setHasShownSkillstreeCompletionModal] = useState<boolean>(false);
  const [showPassportModal, setShowPassportModal] = useState<boolean>(false);
  const [hasShownPassportModal, setHasShownPassportModal] = useState<boolean>(false);
  const [skillstreeData, setSkillstreeData] = useState<any>(null);
  const [passportNames, setPassportNames] = useState<string[]>([]);
  const [showNextVideoOverlay, setShowNextVideoOverlay] = useState<boolean>(false);
  const [videosWithProgress, setVideosWithProgress] = useState<Map<number, any>>(new Map()); // Store videos by video_id with their API data
  const [nextVideoInfo, setNextVideoInfo] = useState<{ title: string; duration: number; videoId: number } | null>(null);
  const [countdown, setCountdown] = useState<number>(5);
  const [hasSubscription, setHasSubscription] = useState<boolean>(false);
  const [isSkillstreeCompleted, setIsSkillstreeCompleted] = useState<boolean>(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState<boolean>(false);
  const [showPlayButtonOverlay, setShowPlayButtonOverlay] = useState<boolean>(true);
  const [thumbnailLoadError, setThumbnailLoadError] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressUpdateIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastCompletionStateRef = useRef<boolean>(false);
  const isProceedingToNextRef = useRef<boolean>(false); // Prevent multiple calls to loadVideo
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasScrolledToTopRef = useRef<boolean>(false);

  // Helper function to process array in batches with delay
  const processBatch = async <T, R>(
    items: T[],
    batchSize: number,
    delayMs: number,
    processor: (item: T) => Promise<R | null>
  ): Promise<R[]> => {
    const results: R[] = [];
    
    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize);
      const batchPromises = batch.map(processor);
      const batchResults = await Promise.all(batchPromises);
      
      // Filter out null results and add to results array
      batchResults.forEach((result) => {
        if (result !== null) {
          results.push(result);
        }
      });
      
      // Add delay between batches (except for the last batch)
      if (i + batchSize < items.length) {
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }
    
    return results;
  };

  // Function to fetch progress for all videos (batched to avoid too many API calls)
  const fetchAllVideosProgress = async (videos: any[]): Promise<Map<number, { completion_rate: number; isCompleted: boolean }>> => {
    const progressMap = new Map<number, { completion_rate: number; isCompleted: boolean }>();
    
    // Filter videos that have resource_id
    const videosWithResourceId = videos.filter((video: any) => video.video?.resource_id);
    
    if (videosWithResourceId.length === 0) {
      return progressMap;
    }
    
    // Process videos in batches of 3 with 200ms delay between batches
    // This prevents overwhelming the API with too many simultaneous requests
    const batchSize = 3;
    const delayBetweenBatches = 200; // milliseconds
    
    const results = await processBatch(
      videosWithResourceId,
      batchSize,
      delayBetweenBatches,
      async (video: any) => {
        const resourceId = video.video?.resource_id;
        if (!resourceId) return null;
        
        try {
          const response = await videoApi.getVideoProgress(resourceId);
          const progressData = (response.data as any).data;
          if (progressData) {
            // Ensure completion_rate is a number and handle edge cases
            const completionRate = typeof progressData.completion_rate === 'number' 
              ? progressData.completion_rate 
              : parseFloat(progressData.completion_rate) || 0;
            // Video is considered completed ONLY at 95% or higher (strict check)
            const isCompleted = completionRate >= 95;
            return { resourceId, completionRate, isCompleted };
          }
        } catch (err) {
          // If no progress exists, API might return 404, which is fine - video is not completed
          return { resourceId, completionRate: 0, isCompleted: false };
        }
        return null;
      }
    );
    
    // Populate the map with results
    results.forEach((result) => {
      if (result) {
        progressMap.set(result.resourceId, {
          completion_rate: result.completionRate,
          isCompleted: result.isCompleted
        });
      }
    });
    
    return progressMap;
  };

  // Function to fetch and update microskill data
  const fetchMicroskillData = async (shouldFetchVideo: boolean = true): Promise<number | null> => {
    if (!categoryId || !skillstreeId || !microskillId) {
      return null;
    }

    try {
      // Fetch skillstree data to get category name, skillstree name, and all microskills
      let skillstreeData: any = null;
      let nextMicroskillData: any = null;
      let currentMicroskillFromSkillstree: any = null; // Store current microskill data from skillstree
      
      try {
        const skillstreeResponse = await skillstreeApi.getSkillstreeById(skillstreeId);
        skillstreeData = (skillstreeResponse.data as any).data;
        
        // Store skillstree data in state
        setSkillstreeData(skillstreeData);
        
        if (skillstreeData) {
          // Set category and skillstree names
          setCategoryTitle(skillstreeData.core_category_name || 'Category');
          setSkillstreeName(skillstreeData.skillstree_name || 'Skillstree');
          
          // Find current microskill and get next one
          const firstVersion = skillstreeData.versions && skillstreeData.versions.length > 0 
            ? skillstreeData.versions[0] 
            : null;
          
          if (firstVersion && firstVersion.micro_skills && Array.isArray(firstVersion.micro_skills)) {
            const currentMicroskillId = parseInt(microskillId, 10);
            const currentIndex = firstVersion.micro_skills.findIndex(
              (ms: any) => ms.micro_skill_id === currentMicroskillId
            );
            
            // Get the current microskill data from skillstree (includes learning_status and learning_progress)
            if (currentIndex >= 0) {
              currentMicroskillFromSkillstree = firstVersion.micro_skills[currentIndex];
            }
            
            // Get the next microskill if available
            if (currentIndex >= 0 && currentIndex < firstVersion.micro_skills.length - 1) {
              nextMicroskillData = firstVersion.micro_skills[currentIndex + 1];
            }
          }
        }
      } catch (err) {
        console.warn('Error fetching skillstree data:', err);
        // Continue with microskill fetch even if skillstree fetch fails
      }

      // Call the API endpoint
      const response = await microskillApi.getMicroskillById(microskillId);
      const apiData = (response.data as any).data; // API returns { status, data: {...}, message }

      if (!apiData) {
        throw new Error('No data received from API');
      }

      // Get the first version (or you can show all versions)
      const firstVersion = apiData.versions && apiData.versions.length > 0 ? apiData.versions[0] : null;
      
      if (!firstVersion) {
        throw new Error('No versions found for this microskill');
      }

      // Calculate total duration from all videos
      const totalDurationSeconds = firstVersion.videos 
        ? firstVersion.videos.reduce((sum: number, video: any) => sum + (video.video?.duration || 0), 0)
        : 0;
      const totalHours = Math.round(totalDurationSeconds / 3600 * 10) / 10;

      // Calculate total viewers
      const totalViewers = firstVersion.videos 
        ? firstVersion.videos.reduce((sum: number, video: any) => sum + (video.total_unique_view || 0), 0)
        : 0;

      // Get trainer name from first video (or combine multiple trainers)
      const trainerName = firstVersion.videos && firstVersion.videos.length > 0
        ? firstVersion.videos[0].trainer_name || 'Multiple Instructors'
        : 'Multiple Instructors';

      // Calculate course progress from videos
      const videos = firstVersion?.videos || [];
      const totalCount = videos.length;
      
      // Fetch progress for all videos
      const videosProgressMap = await fetchAllVideosProgress(videos);
      
      // Find the current video (first incomplete video, or first video if none completed)
      let currentVideoIndex = 0;
      for (let i = 0; i < videos.length; i++) {
        const video = videos[i];
        const resourceId = video.video?.resource_id;
        if (resourceId) {
          const progress = videosProgressMap.get(resourceId);
          if (!progress || !progress.isCompleted) {
            currentVideoIndex = i;
            break;
          }
        }
      }
      
      // Map videos to microlearning modules (each video becomes a microlearning module)
      const microlearningModules: MicrolearningModule[] = videos.map((video: any, index: number) => {
        // Calculate duration from this video
        const videoDurationSeconds = video.video?.duration || 0;
        const videoHours = Math.round(videoDurationSeconds / 3600 * 10) / 10;
        
        // Determine completion status - check API response first, then fall back to videoProgress
        const resourceId = video.video?.resource_id;
        const videoProgress = resourceId ? videosProgressMap.get(resourceId) : null;
        
        // Primary check: Use learning_status from API response if available
        // Handle both string and number for learning_progress
        const learningProgressNum = typeof video.learning_progress === 'number' 
          ? video.learning_progress 
          : parseFloat(video.learning_progress) || 0;
        const isCompletedFromApi = video.learning_status === 'completed' || learningProgressNum >= 100;
        
        // Also check videoProgress from fetchAllVideosProgress
        const isCompletedFromProgress = videoProgress?.isCompleted === true || (videoProgress?.completion_rate !== undefined && videoProgress.completion_rate >= 95);
        
        // Video is completed if either source says it's completed
        const isCompleted = isCompletedFromApi || isCompletedFromProgress;
        const isCurrent = index === currentVideoIndex;
        
        // Debug logging to help identify completion issues
        console.log(`Video ${index + 1} (${video.title || video.video?.name}): video_id=${video.video_id}, resource_id=${resourceId}, learning_status=${video.learning_status}, learning_progress=${video.learning_progress} (${learningProgressNum}), completion_rate=${videoProgress?.completion_rate}%, isCompletedFromApi=${isCompletedFromApi}, isCompletedFromProgress=${isCompletedFromProgress}, final_isCompleted=${isCompleted}`);
        
        return {
          id: index + 1, // Use video index + 1 (1, 2, 3, 4...)
          title: video.title || video.video?.name || `Video ${index + 1}`,
          duration: videoHours,
          completed: isCompleted,
          current: isCurrent,
          videoId: video.video_id, // Store video_id for navigation
          thumbnail: video.thumbnail?.storage_path || undefined, // Store thumbnail storage_path
        };
      });

      // Store videos data with their progress for later reference
      const videosMap = new Map<number, any>();
      videos.forEach((video: any) => {
        if (video.video_id) {
          const resourceId = video.video?.resource_id;
          const videoProgressData = resourceId ? videosProgressMap.get(resourceId) : null;
          videosMap.set(video.video_id, {
            ...video,
            videoProgress: videoProgressData
          });
        }
      });
      setVideosWithProgress(videosMap);

      // Calculate overall progress based on videos
      const completedCount = microlearningModules.filter((m: MicrolearningModule) => m.completed).length;
      const courseProgress = {
        completed: completedCount,
        total: totalCount,
        remaining: Math.max(0, totalCount - completedCount)
      };

      // Build next microskill data if available
      let nextMicroskillObj: NextMicroskill = {
        id: 0,
        code: '',
        title: 'Next Microskill',
        duration: 0,
        instructors: 'Multiple Instructor',
        microlearningCount: 0,
        subModules: []
      };

      // Get next microskill from skillstree data
      if (nextMicroskillData) {
        // Check if microskill has videos
        const hasVideos = nextMicroskillData.videos && Array.isArray(nextMicroskillData.videos) && nextMicroskillData.videos.length > 0;
        
        if (hasVideos) {
          const nextVideos = nextMicroskillData.videos;
          const nextTotalDurationSeconds = nextVideos.reduce(
            (sum: number, video: any) => sum + (video.video?.duration || 0), 
            0
          );
          const nextTotalHours = Math.round(nextTotalDurationSeconds / 3600 * 10) / 10;

          // Get trainer name from first video
          const nextTrainerName = nextVideos.length > 0
            ? nextVideos[0].trainer_name || 'Multiple Instructors'
            : 'Multiple Instructors';

          // Map videos to subModules - thumbnails from skillstree API have 'url' property
          const subModules = nextVideos.map((video: any, index: number) => {
            // Access thumbnail URL from skillstree API: video.thumbnail.url
            const thumbnailUrl = video.thumbnail?.url || video.thumbnail?.storage_path;
            return {
              id: index + 1,
              title: video.title || video.video?.name || `Video ${index + 1}`,
              trainer: nextTrainerName,
              views: video.total_unique_view || 0,
              thumbnail: thumbnailUrl || undefined
            };
          });

          nextMicroskillObj = {
            id: nextMicroskillData.micro_skill_id || 0,
            code: nextMicroskillData.unique_code || '',
            title: nextMicroskillData.title || 'Next Microskill',
            duration: nextTotalHours,
            instructors: nextTrainerName,
            microlearningCount: nextVideos.length,
            imageUrl: nextMicroskillData.image_url || undefined,
            isFree: nextMicroskillData.is_free !== undefined ? nextMicroskillData.is_free : true,
            canAccess: nextMicroskillData.can_access !== undefined ? nextMicroskillData.can_access : true,
            subModules: subModules
          };
        } else {
          // Handle case where next microskill exists but has no videos (locked content)
          nextMicroskillObj = {
            id: nextMicroskillData.micro_skill_id || 0,
            code: nextMicroskillData.unique_code || '',
            title: nextMicroskillData.title || 'Next Microskill',
            duration: 0,
            instructors: 'Multiple Instructor',
            microlearningCount: 0,
            imageUrl: nextMicroskillData.image_url || undefined,
            isFree: nextMicroskillData.is_free !== undefined ? nextMicroskillData.is_free : true,
            canAccess: nextMicroskillData.can_access !== undefined ? nextMicroskillData.can_access : true,
            subModules: []
          };
        }
      }

      // Create microskill detail object
      // Use learning_status and learning_progress from skillstree data if available (more accurate)
      // Fallback to microskill API data if skillstree data is not available
      const learningStatus = currentMicroskillFromSkillstree?.learning_status || firstVersion.learning_status || undefined;
      const learningProgress = currentMicroskillFromSkillstree?.learning_progress || firstVersion.learning_progress || 0;
      
      console.log('Microskill completion data:', {
        microskillId: apiData.micro_skill_id,
        fromSkillstree: {
          learning_status: currentMicroskillFromSkillstree?.learning_status,
          learning_progress: currentMicroskillFromSkillstree?.learning_progress
        },
        fromMicroskillApi: {
          learning_status: firstVersion.learning_status,
          learning_progress: firstVersion.learning_progress
        },
        final: {
          learning_status: learningStatus,
          learning_progress: learningProgress
        }
      });
      
      const microskillData: MicroskillDetail = {
        id: apiData.micro_skill_id,
        code: apiData.unique_code || '',
        name: firstVersion.version || 'Unnamed Microskill',
        title: firstVersion.title || 'Unnamed Microskill',
        description: apiData.description || '',
        duration: totalHours,
        totalViewers: totalViewers,
        trainer: trainerName,
        skillTag: 'Productivity Skill', // Not in API, using placeholder
        sessionCount: firstVersion.sessionCount || 0,
        learningProgress: learningProgress,
        learningStatus: learningStatus, // Use learning_status from skillstree data (more accurate)
        imageUrl: currentMicroskillFromSkillstree?.image_url || apiData.image_url || undefined,
        courseProgress: courseProgress,
        microlearningModules: microlearningModules,
        nextMicroskill: nextMicroskillObj
      };

      setMicroskill(microskillData);
      
      // Store videos list for navigation
      setVideosList(videos);

      // Return current video ID if available and shouldFetchVideo is true
      if (shouldFetchVideo && videos && videos.length > 0) {
        const currentVideo = videos[currentVideoIndex];
        if (currentVideo && currentVideo.video_id) {
          return currentVideo.video_id;
        }
        // Fallback to first video if current video doesn't have an ID
        if (videos[0] && videos[0].video_id) {
          return videos[0].video_id;
        }
      }
      return null;
    } catch (err) {
      console.error('Error fetching microskill:', err);
      setMicroskill(null);
      return null;
    }
  };

  // Helper function to check if URL is an S3 URL
  const isS3Url = (url: string): boolean => {
    return url.includes('s3.amazonaws.com') || url.includes('s3.ap-southeast-1.amazonaws.com');
  };

  // Helper function to construct full image URL
  const getFullImageUrl = (imageUrl: string): string => {
    // If already a full URL, return as is
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    
    // If relative path starting with /storage/ or /public/, use backend URL
    if (imageUrl.startsWith('/storage/') || imageUrl.startsWith('/public/')) {
      const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost/api';
      const backendUrl = apiBaseUrl.replace('/api', ''); // Remove /api to get backend base URL
      return `${backendUrl}${imageUrl}`;
    }
    
    // Otherwise, construct relative to current origin
    return `${window.location.origin}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
  };

  // Helper function to construct full thumbnail URL
  // Uses direct S3 URL from API response (S3 bucket needs CORS configured)
  const getFullThumbnailUrl = (thumbnailPath: string): string => {
    // If already a full URL, return as is
    if (thumbnailPath.startsWith('http://') || thumbnailPath.startsWith('https://')) {
      return thumbnailPath;
    }
    
    // Thumbnails are stored in AWS S3
    // S3 base URL (can be overridden via environment variable)
    const s3BaseUrl = import.meta.env.VITE_S3_BASE_URL || 'https://dev-skillsture.s3.ap-southeast-1.amazonaws.com';
    
    // Handle S3 thumbnail paths
    // API returns storage_path like "thumbnails/big-data/IS1 BD B/Slide1.PNG"
    if (thumbnailPath.startsWith('thumbnails/')) {
      return `${s3BaseUrl}/${thumbnailPath}`;
    } else if (thumbnailPath.startsWith('/thumbnails/')) {
      // Remove leading slash if present
      const s3Path = thumbnailPath.replace(/^\/+/, '');
      return `${s3BaseUrl}/${s3Path}`;
    }
    
    // If relative path starting with /storage/, use backend URL
    if (thumbnailPath.startsWith('/storage/') || thumbnailPath.startsWith('/public/')) {
      const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost/api';
      const backendUrl = apiBaseUrl.replace('/api', ''); // Remove /api to get backend base URL
      return `${backendUrl}${thumbnailPath}`;
    }
    
    // Otherwise, construct relative to current origin
    return `${window.location.origin}${thumbnailPath.startsWith('/') ? '' : '/'}${thumbnailPath}`;
  };

  // Helper function to format title: replace underscores with spaces and capitalize first letter of each word
  const formatTitle = (title: string): string => {
    if (!title) return '';
    // Replace underscores with spaces
    const withSpaces = title.replace(/_/g, ' ');
    // Capitalize first letter of each word
    return withSpaces
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Helper function to format countdown time
  const formatCountdown = (seconds: number): string => {
    // If less than 60 seconds, show as "X seconds"
    if (seconds < 60) {
      return `${seconds} second${seconds !== 1 ? 's' : ''}`;
    }
    // Otherwise show as MM:SS format
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Helper function to construct full video URL
  // Uses direct S3 URL from API response (S3 bucket needs CORS configured)
  const getFullVideoUrl = (videoUrl: string, videoId?: number): string => {
    // If already a full URL (from API response), return as is
    if (videoUrl.startsWith('http://') || videoUrl.startsWith('https://')) {
      return videoUrl;
    }
    
    // Videos are stored in AWS S3
    // S3 base URL (can be overridden via environment variable)
    const s3BaseUrl = import.meta.env.VITE_S3_BASE_URL || 'https://dev-skillsture.s3.ap-southeast-1.amazonaws.com';
    
    // Handle S3 video paths
    // API might return /storage/videos/... or /videos/...
    // S3 stores videos at videos/... (without /storage prefix)
    if (videoUrl.startsWith('/storage/videos/')) {
      // Remove /storage prefix and prepend S3 URL
      const s3Path = videoUrl.replace('/storage/videos/', 'videos/');
      return `${s3BaseUrl}/${s3Path}`;
    } else if (videoUrl.startsWith('/videos/')) {
      // Already in correct format, just prepend S3 URL
      const s3Path = videoUrl.replace(/^\/+/, ''); // Remove leading slash
      return `${s3BaseUrl}/${s3Path}`;
    }
    
    // If relative path starting with /storage/ (but not videos), it's likely from Laravel backend
    // Use backend URL (API_BASE_URL without /api)
    if (videoUrl.startsWith('/storage/') || videoUrl.startsWith('/public/')) {
      const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost/api';
      const backendUrl = apiBaseUrl.replace('/api', ''); // Remove /api to get backend base URL
      return `${backendUrl}${videoUrl}`;
    }
    
    // Otherwise, construct relative to current origin
    return `${window.location.origin}${videoUrl.startsWith('/') ? '' : '/'}${videoUrl}`;
  };

  // Helper function to diagnose video URL issues
  const diagnoseVideoUrl = async (videoUrl: string) => {
    try {
      console.log('🔍 Diagnosing video URL:', videoUrl);
      
      // Test 1: Check if URL is accessible via fetch (HEAD request)
      try {
        const response = await fetch(videoUrl, {
          method: 'HEAD',
          mode: 'no-cors', // This will always succeed but won't tell us about CORS
        });
        console.log('✅ URL is accessible (no-cors mode)');
      } catch (err) {
        console.log('❌ URL not accessible:', err);
      }
      
      // Test 2: Try to fetch with CORS (this will fail if CORS is not configured)
      try {
        const response = await fetch(videoUrl, {
          method: 'HEAD',
          mode: 'cors',
        });
        console.log('✅ CORS is properly configured');
        console.log('Response headers:', [...response.headers.entries()]);
      } catch (err: any) {
        if (err.message?.includes('CORS') || err.message?.includes('cross-origin')) {
          console.error('❌ CORS ERROR DETECTED:', err.message);
          return { type: 'CORS', message: err.message };
        }
        console.warn('⚠️ CORS test failed (may be network issue):', err.message);
      }
      
      // Test 3: Check URL origin vs current origin
      const videoOrigin = new URL(videoUrl, window.location.href).origin;
      const currentOrigin = window.location.origin;
      console.log('Video Origin:', videoOrigin);
      console.log('Current Origin:', currentOrigin);
      
      if (videoOrigin !== currentOrigin) {
        console.warn('⚠️ Cross-origin request detected - CORS headers required');
      } else {
        console.log('✅ Same-origin request - no CORS needed');
      }
      
      return null;
    } catch (err: any) {
      console.error('Error diagnosing video URL:', err);
      return { type: 'NETWORK', message: err.message };
    }
  };

  // Function to fetch video progress (for currently playing video)
  const fetchVideoProgress = async (videoResourceId: number) => {
    try {
      const response = await videoApi.getVideoProgress(videoResourceId);
      const progressData = (response.data as any).data;
      if (progressData) {
        const completionRate = progressData.completion_rate || 0;
        const isCompleted = completionRate >= 95; // Video is considered completed at 95%+
        lastCompletionStateRef.current = isCompleted;
        setVideoProgress({
          completion_rate: completionRate,
          isCompleted: isCompleted
        });
      } else {
        // No progress found, initialize as not completed
        lastCompletionStateRef.current = false;
        setVideoProgress({
          completion_rate: 0,
          isCompleted: false
        });
      }
    } catch (err) {
      // If no progress exists, API might return 404, which is fine
      setVideoProgress({
        completion_rate: 0,
        isCompleted: false
      });
    }
  };

  // Function to load and play a selected video
  const loadVideo = async (videoId: number) => {
    // Prevent multiple calls
    if (isProceedingToNextRef.current) {
      return;
    }

    // Don't reload if it's the same video
    if (videoData?.video_id === videoId) {
      return;
    }

    // Set flag to prevent multiple calls
    isProceedingToNextRef.current = true;

    try {
      // Clear any existing countdown interval
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      setShowNextVideoOverlay(false);
      setNextVideoInfo(null);
      
      setVideoLoading(true);
      setVideoError(false);
      setVideoErrorDetails(null);
      // Clear videoProgress immediately when loading a new video to prevent stale data
      setVideoProgress(null);
      // Reset play button overlay for new video
      setShowPlayButtonOverlay(true);
      const response = await videoApi.getVideoById(videoId);
      const apiVideoData = (response.data as any).data;
      if (apiVideoData) {
        setVideoData(apiVideoData);
        
        // Fetch video progress if we have a resource_id
        if (apiVideoData.video?.resource_id) {
          fetchVideoProgress(apiVideoData.video.resource_id);
        }
        
        // Reset flag after video is loaded
        isProceedingToNextRef.current = false;
      } else {
        isProceedingToNextRef.current = false;
      }
    } catch (err: any) {
      console.error('Error fetching video:', err);
      setVideoError(true);
      setVideoErrorDetails({
        type: err.code === 'ERR_NETWORK' ? 'NETWORK' : 'UNKNOWN',
        message: err.response?.data?.message || err.message || 'Unknown error',
      });
      isProceedingToNextRef.current = false;
    } finally {
      setVideoLoading(false);
    }
  };

  // Function to update video progress
  const updateVideoProgress = async (videoResourceId: number, currentTime: number, duration: number) => {
    if (!duration || duration === 0) return;
    
    const completionRate = Math.min(100, (currentTime / duration) * 100);
    const isCompleted = completionRate >= 95;
    const wasCompleted = lastCompletionStateRef.current;
    
    try {
      await videoApi.updateVideoProgress({
        video_resource_id: videoResourceId,
        completion_rate: Math.round(completionRate * 100) / 100,
        last_stopped_at: new Date().toISOString()
      });
      
      // Update ref to track completion state
      const justCompleted = !wasCompleted && isCompleted;
      lastCompletionStateRef.current = isCompleted;
      
      setVideoProgress({
        completion_rate: completionRate,
        isCompleted: isCompleted
      });

      // If video just reached completion (wasn't completed before, but is now), check if microskill is completed
      if (justCompleted) {
        // Use setTimeout to ensure state updates are processed before checking completion
        setTimeout(async () => {
          // Immediately check if all modules are now completed (before refetch)
          if (microskill && microskill.microlearningModules) {
            // CRITICAL: Verify that the microskill ID matches the current route parameter
            // This prevents showing the achievement modal for a different microskill when navigating
            if (!microskillId) {
              return; // No microskillId in route - can't verify, skip check
            }
            const currentMicroskillId = parseInt(microskillId, 10);
            if (microskill.id !== currentMicroskillId) {
              // Microskill data doesn't match current route - don't show modal
              console.log('Microskill ID mismatch - skipping achievement modal check', {
                microskillId: microskill.id,
                currentRouteId: currentMicroskillId
              });
              return;
            }
            
            // Create adjusted modules list with current video marked as completed
            const adjustedModules = microskill.microlearningModules.map((module) => {
              // Check if this is the currently playing video
              const isCurrentlyPlayingThisModule = videoData?.video_id === module.videoId;
              
              // If this is the currently playing video, mark it as completed (using latest videoProgress state)
              if (isCurrentlyPlayingThisModule) {
                // Use the isCompleted we just set, or check videoProgress state
                return { ...module, completed: true }; // We know it's completed because justCompleted is true
              }
              
              // For other videos, use stored completion status from videosWithProgress
              const storedVideoData = videosWithProgress.get(module.videoId);
              if (storedVideoData) {
                const learningProgressNum = typeof storedVideoData.learning_progress === 'number' 
                  ? storedVideoData.learning_progress 
                  : parseFloat(String(storedVideoData.learning_progress)) || 0;
                const isCompletedFromApi = storedVideoData.learning_status === 'completed' || learningProgressNum >= 100;
                const isCompletedFromProgress = storedVideoData.videoProgress?.isCompleted === true || 
                  (storedVideoData.videoProgress?.completion_rate !== undefined && storedVideoData.videoProgress.completion_rate >= 95);
                return { ...module, completed: isCompletedFromApi || isCompletedFromProgress };
              }
              
              // Fallback to module's stored completion status
              return module;
            });
            
            // Check if ALL modules are completed
            const allModulesCompleted = adjustedModules.length > 0 && 
              adjustedModules.every(module => module.completed === true);
            
            // Also check API status from skillstree data if available
            const isCompletedFromApi = microskill.learningStatus === 'completed' || microskill.learningProgress >= 100;
            
            // Show achievement modal immediately if all modules are completed
            if ((isCompletedFromApi || allModulesCompleted) && !hasShownAchievementModal) {
              console.log('Microskill completed immediately!', {
                microskillId: microskill.id,
                learningStatus: microskill.learningStatus,
                learningProgress: microskill.learningProgress,
                isCompletedFromApi,
                allModulesCompleted,
                totalModules: adjustedModules.length,
                completedModules: adjustedModules.filter(m => m.completed).length,
                currentVideoId: videoData?.video_id
              });
              setShowAchievementModal(true);
              setHasShownAchievementModal(true);
              await markAchievementModalAsShown(microskill.id, user?.id);
            }
          }
        }, 100); // Small delay to ensure state updates are processed
        
        // Refetch microskill data to update progress bars (without reloading video)
        fetchMicroskillData(false).catch((err) => {
          console.error('Error refreshing microskill data:', err);
        });
      }
    } catch (err) {
      console.error('Error updating video progress:', err);
    }
  };

  // Check subscription status
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      try {
        const response = await subscriptionService.getUserSubscriptions();
        const activeSubscriptions = response.subscriptions?.filter(sub => sub.is_active) || [];
        const hasActiveSubscription = activeSubscriptions.length > 0;
        setHasSubscription(hasActiveSubscription);
      } catch (error) {
        console.error('Failed to check subscription status:', error);
        setHasSubscription(false);
      }
    };

    checkSubscriptionStatus();
  }, []);

  // Prevent browser scroll restoration on page refresh
  useEffect(() => {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    return () => {
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'auto';
      }
    };
  }, []);

  // Scroll to top when route params change or on initial mount
  useEffect(() => {
    // Use a combination of requestAnimationFrame and setTimeout to ensure scroll happens 
    // after all state initialization (including localStorage checks) is complete
    // This prevents layout shifts from causing incorrect scroll positions
    const scrollTimer = setTimeout(() => {
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        hasScrolledToTopRef.current = true;
      });
    }, 0);
    
    return () => {
      clearTimeout(scrollTimer);
    };
  }, [categoryId, skillstreeId, microskillId]);

  // Reset thumbnail load error when videoData changes
  useEffect(() => {
    setThumbnailLoadError(false);
  }, [videoData]);

  // Handle fullscreen events to ensure video is fully contained
  useEffect(() => {
    const handleFullscreenChange = () => {
      const video = videoRef.current;
      if (!video) return;

      // Check if video is in fullscreen mode
      const isFullscreen = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement ||
        (video as any).webkitDisplayingFullscreen
      );

      if (isFullscreen) {
        // Apply contain style to prevent cropping
        video.style.objectFit = 'contain';
        video.style.width = '100vw';
        video.style.height = '100vh';
        video.style.maxWidth = '100vw';
        video.style.maxHeight = '100vh';
        video.style.background = '#000';
      } else {
        // Reset to cover for normal view
        video.style.objectFit = '';
        video.style.width = '';
        video.style.height = '';
        video.style.maxWidth = '';
        video.style.maxHeight = '';
        video.style.background = '';
      }
    };

    // Listen for fullscreen changes
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    // Also listen for video-specific fullscreen events (iOS Safari)
    const video = videoRef.current;
    if (video) {
      video.addEventListener('webkitbeginfullscreen', handleFullscreenChange);
      video.addEventListener('webkitendfullscreen', handleFullscreenChange);
    }

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
      if (video) {
        video.removeEventListener('webkitbeginfullscreen', handleFullscreenChange);
        video.removeEventListener('webkitendfullscreen', handleFullscreenChange);
      }
    };
  }, [videoData]); // Re-run when video changes

  // Reset achievement modal state when microskill changes and check API/localStorage
  useEffect(() => {
    const checkModal = async () => {
      if (microskillId) {
        const microskillIdNum = parseInt(microskillId, 10);
        const hasShown = await hasShownAchievementModalForMicroskill(microskillIdNum, user?.id);
        setHasShownAchievementModal(hasShown);
        setShowAchievementModal(false);
        // Reset videoProgress immediately when microskill changes to prevent stale completion data
        // from triggering achievement modal for the new microskill
        setVideoProgress(null);
        lastCompletionStateRef.current = false;
      } else {
        setHasShownAchievementModal(false);
        setShowAchievementModal(false);
        setVideoProgress(null);
        lastCompletionStateRef.current = false;
      }
    };
    checkModal();
  }, [microskillId, user?.id]);

  // Reset skillstree completion modal state when skillstree changes and check API/localStorage
  useEffect(() => {
    const checkModal = async () => {
      if (skillstreeId) {
        const skillstreeIdNum = parseInt(skillstreeId, 10);
        const hasShown = await hasShownSkillstreeCompletionModalForSkillstree(skillstreeIdNum, user?.id);
        setHasShownSkillstreeCompletionModal(hasShown);
        setShowSkillstreeCompletionModal(false);
      } else {
        setHasShownSkillstreeCompletionModal(false);
        setShowSkillstreeCompletionModal(false);
      }
    };
    checkModal();
  }, [skillstreeId, user?.id]);

  // Reset passport modal state when skillstree changes and check API/localStorage
  useEffect(() => {
    const checkModal = async () => {
      if (skillstreeId) {
        const skillstreeIdNum = parseInt(skillstreeId, 10);
        const hasShown = await hasShownPassportModalForSkillstree(skillstreeIdNum, user?.id);
        setHasShownPassportModal(hasShown);
        setShowPassportModal(false);
      } else {
        setHasShownPassportModal(false);
        setShowPassportModal(false);
      }
    };
    checkModal();
  }, [skillstreeId, user?.id]);

  useEffect(() => {
    const fetchMicroskill = async () => {
      if (!categoryId || !skillstreeId || !microskillId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const videoId = await fetchMicroskillData(true);
        
        // After fetching microskill data, fetch the video if available
        if (videoId) {
          fetchVideo(videoId);
        }
      } catch (err) {
        console.error('Error in fetchMicroskill:', err);
        setMicroskill(null);
      } finally {
        setLoading(false);
      }
    };

    const fetchVideo = async (videoId: number) => {
      try {
        setVideoLoading(true);
        setVideoError(false);
        setVideoErrorDetails(null);
        // Reset play button overlay for new video
        setShowPlayButtonOverlay(true);
        const response = await videoApi.getVideoById(videoId);
        const apiVideoData = (response.data as any).data;
        if (apiVideoData) {
          setVideoData(apiVideoData);
          
          // Log video URL for debugging and diagnose potential issues
          if (apiVideoData.video?.url) {
            const originalUrl = apiVideoData.video.url;
            const fullUrl = getFullVideoUrl(originalUrl);
            const isS3 = isS3Url(fullUrl);
            console.log('📹 Video URL Information:');
            console.log('  Original URL:', originalUrl);
            console.log('  Full URL (constructed):', fullUrl);
            console.log('  Storage Type:', isS3 ? 'AWS S3' : 'Local/Backend');
            console.log('  Video URL origin:', new URL(fullUrl, window.location.href).origin);
            console.log('  Current origin:', window.location.origin);
            if (isS3) {
              console.log('  ℹ️ S3 videos require CORS configuration on the bucket');
              console.log('  ℹ️ If CORS errors occur, check S3 bucket CORS settings');
            }
            
            // Run diagnostic tests
            diagnoseVideoUrl(fullUrl).then((diagnosis) => {
              if (diagnosis) {
                console.warn('⚠️ Pre-emptive diagnosis:', diagnosis);
                // Don't set error state yet - let the video element handle it
                // But log it for debugging
              }
            });
          }
          
          // Fetch video progress if we have a resource_id
          if (apiVideoData.video?.resource_id) {
            fetchVideoProgress(apiVideoData.video.resource_id);
          }
        }
      } catch (err: any) {
        console.error('Error fetching video:', err);
        const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
        const errorType = err.code === 'ERR_NETWORK' ? 'NETWORK' : 'UNKNOWN';
        setVideoError(true);
        setVideoErrorDetails({
          type: errorType,
          message: errorMessage,
        });
      } finally {
        setVideoLoading(false);
      }
    };

    fetchMicroskill();

    // Cleanup interval on unmount
    return () => {
      if (progressUpdateIntervalRef.current) {
        clearTimeout(progressUpdateIntervalRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [categoryId, skillstreeId, microskillId]);

  // Check for skillstree completion and show modal
  useEffect(() => {
    const checkCompletion = async () => {
      if (skillstreeData && skillstreeId) {
        const skillstreeIdNum = parseInt(skillstreeId, 10);
        const firstVersion = skillstreeData.versions && skillstreeData.versions.length > 0 
          ? skillstreeData.versions[0] 
          : null;
        
        if (firstVersion) {
          // Check completion status - handle both string and number for learning_progress
          const learningProgressNum = typeof firstVersion.learning_progress === 'number' 
            ? firstVersion.learning_progress 
            : parseFloat(firstVersion.learning_progress) || 0;
          
          const isCompleted = firstVersion.learning_status === 'completed' || learningProgressNum >= 100;
          
          // Also check if all microskills are completed as a fallback
          let allMicroskillsCompleted = false;
          if (firstVersion.micro_skills && Array.isArray(firstVersion.micro_skills)) {
            const totalMicroskills = firstVersion.micro_skills.length;
            const completedMicroskills = firstVersion.micro_skills.filter(
              (ms: any) => ms.learning_status === 'completed' || ms.learning_progress >= 100
            ).length;
            allMicroskillsCompleted = totalMicroskills > 0 && completedMicroskills === totalMicroskills;
          }
          
          // Update completion status state
          setIsSkillstreeCompleted(isCompleted || allMicroskillsCompleted);
          
          // Check API/localStorage to see if modal has already been shown for this skillstree
          const hasShownInStorage = await hasShownSkillstreeCompletionModalForSkillstree(skillstreeIdNum, user?.id);
          
          // Show modal if skillstree is completed (either by status or all microskills completed)
          // and hasn't been shown before (both state and API/localStorage check)
          if ((isCompleted || allMicroskillsCompleted) && !hasShownSkillstreeCompletionModal && !hasShownInStorage) {
            console.log('Skillstree completed!', {
              learning_status: firstVersion.learning_status,
              learning_progress: firstVersion.learning_progress,
              learningProgressNum,
              isCompleted,
              allMicroskillsCompleted,
              totalMicroskills: firstVersion.micro_skills?.length || 0,
              completedMicroskills: firstVersion.micro_skills?.filter((ms: any) => ms.learning_status === 'completed' || ms.learning_progress >= 100).length || 0
            });
            setShowSkillstreeCompletionModal(true);
            setHasShownSkillstreeCompletionModal(true);
            await markSkillstreeCompletionModalAsShown(skillstreeIdNum, user?.id);
          }
        }
      }
    };
    checkCompletion();
  }, [skillstreeData, hasShownSkillstreeCompletionModal, skillstreeId, user?.id]);

  // Fetch passport names when passport modal is shown
  useEffect(() => {
    if (showPassportModal) {
      const fetchPassportNames = async () => {
        try {
          const response = await dashboardService.getUserProfile();
          const profileData = (response.data as any).data;
          
          // Extract passport names from profile data
          // Adjust the path based on your API response structure
          if (profileData?.passports && Array.isArray(profileData.passports)) {
            const names = profileData.passports.map((passport: any) => 
              passport.name || passport.passport_name || passport.title
            ).filter((name: string) => name); // Filter out empty/null values
            setPassportNames(names);
          } else if (profileData?.passport_names && Array.isArray(profileData.passport_names)) {
            setPassportNames(profileData.passport_names);
          } else {
            // Fallback: try to find passport data in different possible structures
            const passportData = profileData?.passport_data || profileData?.passports_data || profileData?.completed_passports;
            if (passportData && Array.isArray(passportData)) {
              const names = passportData.map((p: any) => p.name || p.passport_name || p.title).filter((name: string) => name);
              setPassportNames(names);
            }
          }
        } catch (err) {
          console.error('Error fetching passport names:', err);
          setPassportNames([]); // Set empty array on error
        }
      };
      
      fetchPassportNames();
    }
  }, [showPassportModal]);

  // Show achievement modal when microskill is completed (check API response status OR all videos completed)
  // This hook must be before any conditional returns to follow Rules of Hooks
  useEffect(() => {
    const checkAchievement = async () => {
      if (microskill) {
        // CRITICAL: Verify that the microskill ID matches the current route parameter
        // This prevents showing the achievement modal for a different microskill when navigating
        if (!microskillId) {
          return; // No microskillId in route - can't verify, skip check
        }
        const currentMicroskillId = parseInt(microskillId, 10);
        if (microskill.id !== currentMicroskillId) {
          // Microskill data doesn't match current route - don't show modal
          console.log('Microskill ID mismatch - skipping achievement modal check', {
            microskillId: microskill.id,
            currentRouteId: currentMicroskillId
          });
          return;
        }
        
        // First check: Is microskill marked as completed in API response?
        const learningProgressNum = typeof microskill.learningProgress === 'number' 
          ? microskill.learningProgress 
          : parseFloat(String(microskill.learningProgress)) || 0;
        const isCompletedFromApi = microskill.learningStatus === 'completed' || learningProgressNum >= 100;
        
        // If API says it's completed, show modal immediately (don't wait for video progress)
        if (isCompletedFromApi && !hasShownAchievementModal) {
          console.log('Microskill completed from API!', {
            microskillId: microskill.id,
            learningStatus: microskill.learningStatus,
            learningProgress: microskill.learningProgress,
            learningProgressNum,
            isCompletedFromApi
          });
          setShowAchievementModal(true);
          setHasShownAchievementModal(true);
          await markAchievementModalAsShown(microskill.id, user?.id);
          return; // Early return - don't check individual videos if API says it's completed
        }
        
        // Second check: Are ALL videos/modules completed? (only if API doesn't say it's completed)
        const currentModuleIndex = microskill.microlearningModules.findIndex(m => m.current);
        const currentModule = currentModuleIndex >= 0 ? microskill.microlearningModules[currentModuleIndex] : null;
        
        // Find which module is currently being played (if any)
        const currentlyPlayingModule = microskill.microlearningModules.find(
          module => videoData?.video_id === module.videoId
        );
        
        // If we have videoProgress but it doesn't belong to any module in this microskill,
        // it's stale data from a previous microskill - ignore it and wait for fresh data
        if (videoProgress !== null && !currentlyPlayingModule) {
          // videoProgress exists but videoData doesn't match any module in current microskill
          // This means we're transitioning between microskills - don't check completion yet
          return;
        }
        
        // If we're playing a video, ensure we have videoProgress data before checking completion
        // This prevents false positives when videoProgress hasn't loaded yet
        if (currentlyPlayingModule && videoProgress === null) {
          // Don't check completion yet - wait for videoProgress to load
          return;
        }
        
        // Create adjusted modules list that includes current video completion status
        // IMPORTANT: Use videoProgress data if available (more accurate than stale microskill data)
        const adjustedModules = microskill.microlearningModules.map((module) => {
          const isCurrentlyPlayingThisModule = videoData?.video_id === module.videoId;
          
          // If this is the currently playing video and we have videoProgress data, use that
          // This ensures we use fresh API data instead of potentially stale microskill data
          if (isCurrentlyPlayingThisModule && videoProgress !== null) {
            // Use videoProgress.isCompleted as the source of truth for the currently playing video
            // If videoProgress says it's not completed (e.g., completion_rate: 0), mark as not completed
            return { ...module, completed: videoProgress.isCompleted === true || (videoProgress.completion_rate !== undefined && videoProgress.completion_rate >= 95) };
          }
          
          // For non-playing videos, use the completion status from microskill data
          return module;
        });
        
        // Check if ALL modules are actually completed (strict check)
        const allModulesCompleted = adjustedModules.length > 0 && 
          adjustedModules.every(module => {
            // Strict check: module must be explicitly marked as completed === true
            return module.completed === true;
          });
        
        // Show achievement modal if all videos are completed (only if API didn't already mark it as completed)
        if (allModulesCompleted && !hasShownAchievementModal) {
          console.log('Microskill completed - all videos finished!', {
            microskillId: microskill.id,
            learningStatus: microskill.learningStatus,
            learningProgress: microskill.learningProgress,
            learningProgressNum,
            isCompletedFromApi,
            allModulesCompleted,
            totalModules: adjustedModules.length,
            completedModules: adjustedModules.filter(m => m.completed).length
          });
          setShowAchievementModal(true);
          setHasShownAchievementModal(true);
          await markAchievementModalAsShown(microskill.id, user?.id);
        }
      }
    };
    checkAchievement();
  }, [microskill, videoProgress, videoData, hasShownAchievementModal, microskillId, user?.id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-saas-black text-white p-6 flex items-center justify-center">
        <LoadingSpinner text="Loading..." />
      </div>
    );
  }

  if (!microskill) {
    return (
      <div className="min-h-screen bg-saas-black text-white p-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold">Microskill Not Found</h1>
          <button 
            onClick={() => navigate(`/skillstree/${categoryId}/${skillstreeId}`)}
            className="mt-4 px-4 py-2 bg-orange-500 text-white rounded"
          >
            Back to Skillstree
          </button>
        </div>
      </div>
    );
  }

  // Calculate course progress considering video completion status
  // If current video is completed, mark the current module as completed
  const currentModuleIndex = microskill.microlearningModules.findIndex(m => m.current);
  const baseCompletedCount = microskill.courseProgress.completed;
  // Only mark current module as completed if the currently PLAYING video matches the current module's video
  const currentModule = currentModuleIndex >= 0 ? microskill.microlearningModules[currentModuleIndex] : null;
  const isCurrentlyPlayingCurrentModule = currentModule && videoData?.video_id === currentModule.videoId;
  const isCurrentModuleCompleted = currentModuleIndex >= 0 && 
    (microskill.microlearningModules[currentModuleIndex].completed || 
     (isCurrentlyPlayingCurrentModule && videoProgress?.isCompleted && microskill.microlearningModules[currentModuleIndex].current));
  
  // If current module should be marked as completed but isn't in the base count, add it
  const adjustedCompletedCount = isCurrentModuleCompleted && 
    !microskill.microlearningModules[currentModuleIndex].completed
    ? baseCompletedCount + 1
    : baseCompletedCount;
  
  const adjustedCourseProgress = {
    completed: adjustedCompletedCount,
    total: microskill.courseProgress.total,
    remaining: Math.max(0, microskill.courseProgress.total - adjustedCompletedCount)
  };

  // Update modules list to reflect current module completion status and highlight
  // Check completion status from stored videos data for all modules, not just the currently playing one
  // IMPORTANT: Use videoProgress for the currently playing video, and stored videos data for others
  const adjustedModules = microskill.microlearningModules.map((module) => {
    const isCurrentlyPlayingThisModule = videoData?.video_id === module.videoId;
    
    // Update both completion status and current flag based on the actually playing video
    if (isCurrentlyPlayingThisModule) {
      // This is the currently playing video - mark it as current
      const updatedModule = { ...module, current: true };
      
      // If we have fresh videoProgress data, use it as the source of truth
      if (videoProgress !== null) {
        updatedModule.completed = videoProgress.isCompleted === true || (videoProgress.completion_rate !== undefined && videoProgress.completion_rate >= 95);
      }
      
      return updatedModule;
    } else {
      // This is not the currently playing video - check completion from stored videos data
      const storedVideoData = videosWithProgress.get(module.videoId);
      let completed = module.completed; // Default to stored completion status
      
      if (storedVideoData) {
        // Check learning_status and learning_progress from API response
        const learningProgressNum = typeof storedVideoData.learning_progress === 'number' 
          ? storedVideoData.learning_progress 
          : parseFloat(storedVideoData.learning_progress) || 0;
        const isCompletedFromApi = storedVideoData.learning_status === 'completed' || learningProgressNum >= 100;
        
        // Also check videoProgress if available
        const isCompletedFromProgress = storedVideoData.videoProgress?.isCompleted === true || 
          (storedVideoData.videoProgress?.completion_rate !== undefined && storedVideoData.videoProgress.completion_rate >= 95);
        
        completed = isCompletedFromApi || isCompletedFromProgress;
      }
      
      return { ...module, current: false, completed };
    }
  });

  // Calculate overall course progress percentage
  // This represents the percentage of completed modules, not individual video completion
  const overallProgressPercentage = adjustedCourseProgress.total > 0
    ? (adjustedCourseProgress.completed / adjustedCourseProgress.total) * 100
    : 0;

  return (
    <div className="min-h-screen microskill-detail-page">
      <div className="microskill-detail-container">
        {/* Breadcrumbs */}
        <div className="microskill-breadcrumbs">
          <nav className="breadcrumbs-container">
            <img 
              src="/breadcrumb_icon.svg" 
              alt="Dashboard" 
              className="breadcrumb-icon"
            />
            <span className="breadcrumb-separator"> {'>'} </span>
            <button 
              onClick={() => navigate('/skillstree')}
              className="breadcrumb-link"
            >
              Core Categories
            </button>
            <span className="breadcrumb-separator"> {'>'} </span>
            <button 
              onClick={() => navigate(`/skillstree/${categoryId}`)}
              className="breadcrumb-link"
            >
              {categoryTitle}
            </button>
            <span className="breadcrumb-separator"> {'>'} </span>
            <button 
              onClick={() => navigate(`/skillstree/${categoryId}/${skillstreeId}`)}
              className="breadcrumb-link"
            >
              Skillstree
            </button>
            <span className="breadcrumb-separator"> {'>'} </span>
            <span className="breadcrumb-text">Microskill</span>
            <span className="breadcrumb-separator"> {'>'} </span>
            <span className="breadcrumb-text breadcrumb-active">Microlearning</span>
          </nav>
        </div>

        {/* Main Content Layout */}
        <div className="microskill-detail-layout">
          {/* Course Overview Card - Full Width */}
          <div className="microskill-overview-card">
            <div className="microskill-overview-content">
              <div className="microskill-overview-left text-start">
                <h2 className="microskill-overview-title">{microskill.title}</h2>
                <div className="microskill-overview-metadata">
                  {/* <div className="microskill-overview-meta-item">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 2C9.1 2 10 2.9 10 4C10 5.1 9.1 6 8 6C6.9 6 6 5.1 6 4C6 2.9 6.9 2 8 2Z" fill="white"/>
                      <path d="M3 12C3 10.9 3.9 10 5 10H11C12.1 10 13 10.9 13 12V14H3V12Z" fill="white"/>
                      <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2Z" fill="white"/>
                      <path d="M11 10C12.1 10 13 10.9 13 12V14H11V12C11 11.4 10.6 11 10 11C9.4 11 9 11.4 9 12V14H7V12C7 10.9 7.9 10 9 10H11Z" fill="white"/>
                    </svg>
                    <span>Multiple Instructors</span>
                  </div>
                  <span className="microskill-overview-meta-separator">•</span> */}
                  <div className="microskill-overview-meta-item">
                    <span>{microskill.sessionCount} Sessions</span>
                  </div>
                  <span className="microskill-overview-meta-separator">•</span>
                  <div className="microskill-overview-meta-item">
                    <span>{microskill.duration ? `${microskill.duration} Hours` : '0 Hours'}</span>
                  </div>
                </div>
                <p className="microskill-overview-description">
                 {microskill.description}
                </p>
                <div className="microskill-overview-progress">
                  <span className="microskill-overview-progress-label">Progress</span>
                  <div className="microskill-overview-progress-bar">
                    <div className="microskill-overview-progress-fill" style={{ width: `${overallProgressPercentage}%` }}></div>
                  </div>
                  <span className="microskill-overview-progress-percentage">{Math.round(overallProgressPercentage)}%</span>
                </div>
              </div>
              <div className="microskill-overview-right">
                <div className="microskill-overview-badge">
                  <div className="microskill-overview-badge-icon">
                    {overallProgressPercentage === 100 ? (
                      microskill.imageUrl ? (
                        <img 
                          src={getFullImageUrl(microskill.imageUrl)} 
                          alt="Badge" 
                          onError={(e) => {
                            // Hide badge if image fails to load
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      ) : null
                    ) : (
                      <img 
                        src="/lock_badge.svg" 
                        alt="Lock Badge" 
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Area - Two Columns */}
          <div className="microskill-content-wrapper">
            <div className="microskill-detail-main">
              {/* Header Section */}
              <div className="microskill-header">
                <h1 className="microskill-header-title text-start">{formatTitle(videoData?.video?.name || videoData?.title || microskill.title || '')}</h1>
                <div className="microskill-header-metadata">
                  <div className="microskill-header-meta-item">
                    <svg width="24" height="24" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="8" cy="8" r="6" stroke="#00D9FF" strokeWidth="1.5" fill="none"/>
                      <path d="M8 4V8L10 10" stroke="#00D9FF" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    <div className="microskill-meta-content">
                      <span className="microskill-meta-value">{microskill.duration ? `${microskill.duration} Hours` : '0 Hours'}</span>
                      <span className="microskill-meta-label">Total Duration</span>
                    </div>
                  </div>
                  {/* <div className="microskill-header-meta-item">
                    <svg width="24" height="24" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="8" cy="6" r="3" stroke="#00D9FF" strokeWidth="1.5" fill="none"/>
                      <path d="M3 12C3 10.9 3.9 10 5 10H11C12.1 10 13 10.9 13 12V13H3V12Z" stroke="#00D9FF" strokeWidth="1.5" fill="none"/>
                      <path d="M8 9L7 11L9 11L8 9Z" fill="#00D9FF"/>
                    </svg>
                    <div className="microskill-meta-content">
                      <span className="microskill-meta-value">{microskill.totalViewers.toLocaleString()}</span>
                      <span className="microskill-meta-label">Total Viewers</span>
                    </div>
                  </div> */}
                  {/* <div className="microskill-header-meta-item">
                    <svg width="24" height="24" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="8" cy="6" r="3" stroke="#00D9FF" strokeWidth="1.5" fill="none"/>
                      <circle cx="8" cy="6" r="1.5" fill="#00D9FF"/>
                      <path d="M3 12C3 10.9 3.9 10 5 10H11C12.1 10 13 10.9 13 12V13H3V12Z" stroke="#00D9FF" strokeWidth="1.5" fill="none"/>
                    </svg>
                    <div className="microskill-meta-content">
                      <span className="microskill-meta-value">{microskill.trainer}</span>
                      <span className="microskill-meta-label">Trainer</span>
                    </div>
                  </div> */}
                </div>
              </div>

              {/* Video/Slide Player Area */}
              <div className="microskill-player-container">
                <div className="microskill-player-wrapper">
                  <div className="microskill-skill-tag">{microskill.skillTag}</div>
                  <div className="microskill-slide-preview">
                    {/* Next Video Overlay */}
                    {showNextVideoOverlay && nextVideoInfo && (
                      <div className="next-video-overlay">
                        <div className="next-video-overlay-content">
                          <div className="next-video-overlay-icon">
                            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="12" cy="12" r="11" stroke="white" strokeWidth="2" fill="none"/>
                              <path d="M8 6H16C17.1 6 18 6.9 18 8V16C18 17.1 17.1 18 16 18H8C6.9 18 6 17.1 6 16V8C6 6.9 6.9 6 8 6Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                              <path d="M8 6V18" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                              <path d="M16 6V18" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                              <path d="M12 6V18" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="2 2"/>
                            </svg>
                          </div>
                          <div className="next-video-overlay-text">
                            <div className="next-video-overlay-label">Up Next</div>
                            <div className="next-video-overlay-title">Review: {formatTitle(nextVideoInfo.title)}</div>
                            <div className="next-video-overlay-duration">{nextVideoInfo.duration} min</div>
                            <div className="next-video-overlay-countdown">
                              Starts in <span className="next-video-countdown-number">{formatCountdown(countdown)}</span>
                            </div>
                            <div className="next-video-overlay-buttons">
                              <button 
                                className="next-video-start-btn"
                                onClick={() => {
                                  if (countdownIntervalRef.current) {
                                    clearInterval(countdownIntervalRef.current);
                                    countdownIntervalRef.current = null;
                                  }
                                  setShowNextVideoOverlay(false);
                                  loadVideo(nextVideoInfo.videoId);
                                }}
                              >
                                Start
                              </button>
                              <button 
                                className="next-video-cancel-btn"
                                onClick={() => {
                                  if (countdownIntervalRef.current) {
                                    clearInterval(countdownIntervalRef.current);
                                    countdownIntervalRef.current = null;
                                  }
                                  setShowNextVideoOverlay(false);
                                  setNextVideoInfo(null);
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="microskill-slide-image-wrapper">
                      {videoLoading ? (
                        <div className="flex items-center justify-center h-full">
                          <div className="text-white">Loading video...</div>
                        </div>
                      ) : videoError || !videoData || !videoData.video || !videoData.video.url ? (
                        <div className="flex flex-col items-center justify-center h-full min-h-[400px] bg-gray-900 rounded-lg border border-gray-700">
                          {videoErrorDetails ? (
                            <>
                              <div className="text-center mb-4 px-6">
                                <div className="text-white text-lg font-semibold mb-2">
                                  {videoErrorDetails.type === 'CORS' && (
                                    <span className="text-red-400">🚫 CORS Error</span>
                                  )}
                                  {videoErrorDetails.type === 'NETWORK' && (
                                    <span className="text-orange-400">⚠️ Network Error</span>
                                  )}
                                  {videoErrorDetails.type === 'UNKNOWN' && (
                                    <span className="text-gray-400">❓ Unknown Error</span>
                                  )}
                                </div>
                                <div className="text-gray-300 text-sm mb-2">
                                  {videoErrorDetails.message}
                                </div>
                                {videoErrorDetails.url && videoErrorDetails.url !== 'Unknown' && (
                                  <div className="text-gray-500 text-xs mt-2 break-all">
                                    URL: {videoErrorDetails.url}
                                  </div>
                                )}
                                <div className="text-gray-500 text-xs mt-4 pt-4 border-t border-gray-700">
                                  💡 Check browser console (F12) for detailed diagnostics
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className="text-center px-6">
                              <svg 
                                width="64" 
                                height="64" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="1.5"
                                className="text-gray-500 mx-auto mb-4"
                              >
                                <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              <div className="text-gray-400 text-lg font-medium mb-2">No Video Available</div>
                              <div className="text-gray-500 text-sm">This microskill does not have a video at this time.</div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="relative w-full h-full bg-gray-900">
                          <video 
                            ref={videoRef}
                            controls
                            controlsList="nodownload"
                            className="microskill-slide-image w-full h-full"
                            preload="none"
                            playsInline
                            crossOrigin="anonymous"
                            onContextMenu={(e) => e.preventDefault()} // Disable right-click menu
                            onPlay={() => {
                              // Hide overlay when video starts playing
                              setShowPlayButtonOverlay(false);
                            }}
                          onError={async (e) => {
                            const video = e.currentTarget;
                            const error = video.error;
                            const videoUrl = videoData?.video?.url || 'Unknown';
                            
                            let errorType: 'CORS' | 'NETWORK' | 'UNKNOWN' = 'UNKNOWN';
                            let errorMessage = 'Unknown video error';
                            
                            // Network State codes:
                            // NETWORK_EMPTY = 0
                            // NETWORK_IDLE = 1
                            // NETWORK_LOADING = 2
                            // NETWORK_NO_SOURCE = 3
                            
                            // Check Network State first (most reliable indicator)
                            if (video.networkState === 3) {
                              // NETWORK_NO_SOURCE - source not found or couldn't load
                              errorType = 'NETWORK';
                              errorMessage = `Video source not found or cannot be loaded`;
                              
                              // Try to fetch the URL to get actual HTTP status
                              try {
                                const fullUrl = getFullVideoUrl(videoUrl);
                                
                                console.log('🔍 Attempting to fetch video URL to check HTTP status:', fullUrl);
                                
                                const response = await fetch(fullUrl, { 
                                  method: 'HEAD',
                                  mode: 'no-cors' // Use no-cors to avoid CORS blocking the check
                                });
                                
                                // Note: In no-cors mode, we can't read status, but we can try cors mode
                                try {
                                  const corsResponse = await fetch(fullUrl, { method: 'HEAD', mode: 'cors' });
                                  console.log('✅ HTTP Status:', corsResponse.status, corsResponse.statusText);
                                  
                                  if (corsResponse.status === 404) {
                                    errorMessage = `File not found (404): Video file does not exist at ${videoUrl}`;
                                  } else if (corsResponse.status === 403) {
                                    errorMessage = `Access forbidden (403): You don't have permission to access this video`;
                                  } else if (corsResponse.status === 401) {
                                    errorMessage = `Unauthorized (401): Authentication required to access this video`;
                                  } else if (corsResponse.status >= 500) {
                                    errorMessage = `Server error (${corsResponse.status}): Video server encountered an error`;
                                  } else if (!corsResponse.ok) {
                                    errorMessage = `HTTP error (${corsResponse.status}): ${corsResponse.statusText}`;
                                  }
                                } catch (corsErr: any) {
                                  if (corsErr.message?.includes('CORS') || corsErr.message?.includes('cross-origin')) {
                                    errorType = 'CORS';
                                    errorMessage = `CORS error: Video server does not allow cross-origin access. URL: ${videoUrl}`;
                                  } else {
                                    // File might exist but we can't check due to CORS
                                    console.warn('⚠️ Cannot check HTTP status due to CORS, but file might exist');
                                  }
                                }
                              } catch (fetchErr: any) {
                                console.error('Error checking video URL:', fetchErr);
                                if (fetchErr.message?.includes('Failed to fetch') || fetchErr.message?.includes('network')) {
                                  errorMessage = `Network error: Cannot connect to video server. Check if the server is running and the URL is correct.`;
                                }
                              }
                            } else if (error) {
                              // MediaError codes:
                              // MEDIA_ERR_ABORTED = 1
                              // MEDIA_ERR_NETWORK = 2
                              // MEDIA_ERR_DECODE = 3
                              // MEDIA_ERR_SRC_NOT_SUPPORTED = 4
                              
                              switch (error.code) {
                                case MediaError.MEDIA_ERR_NETWORK:
                                  errorType = 'NETWORK';
                                  errorMessage = `Network error: Unable to fetch video from ${videoUrl}`;
                                  break;
                                case MediaError.MEDIA_ERR_ABORTED:
                                  errorType = 'NETWORK';
                                  errorMessage = `Request aborted: Video loading was interrupted`;
                                  break;
                                case MediaError.MEDIA_ERR_DECODE:
                                  errorType = 'NETWORK';
                                  errorMessage = `Decode error: Video format may be corrupted or unsupported`;
                                  break;
                                case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
                                  errorType = 'NETWORK';
                                  errorMessage = `Source not supported: Video format or codec not supported by browser`;
                                  break;
                                default:
                                  errorMessage = `Video error (code ${error.code}): ${error.message || 'Unknown error'}`;
                              }
                              
                              // Additional CORS detection: Check if error message contains CORS-related keywords
                              const errorMsg = error.message?.toLowerCase() || '';
                              if (errorMsg.includes('cors') || errorMsg.includes('cross-origin') || errorMsg.includes('access-control')) {
                                errorType = 'CORS';
                                errorMessage = `CORS error: Video server does not allow cross-origin access. URL: ${videoUrl}`;
                              }
                            } else {
                              // No error object but video failed - likely network issue
                              errorType = 'NETWORK';
                              errorMessage = `Video failed to load. Network State: ${video.networkState} (3 = NO_SOURCE)`;
                            }
                            
                            // Log detailed error information
                            const fullVideoUrl = videoUrl !== 'Unknown' ? getFullVideoUrl(videoUrl) : videoUrl;
                            const isS3 = videoUrl !== 'Unknown' && isS3Url(fullVideoUrl);
                            
                            console.error('=== VIDEO ERROR DETECTION ===');
                            console.error('Error Type:', errorType);
                            console.error('Error Message:', errorMessage);
                            console.error('Video URL (original):', videoUrl);
                            console.error('Full Video URL (constructed):', fullVideoUrl);
                            console.error('Storage Type:', isS3 ? 'AWS S3' : 'Local/Backend');
                            console.error('Video URL Origin:', videoUrl !== 'Unknown' ? new URL(fullVideoUrl, window.location.href).origin : 'N/A');
                            console.error('Current Origin:', window.location.origin);
                            console.error('MediaError Code:', error?.code);
                            console.error('MediaError Message:', error?.message);
                            console.error('Network State:', video.networkState, `(${video.networkState === 0 ? 'EMPTY' : video.networkState === 1 ? 'IDLE' : video.networkState === 2 ? 'LOADING' : 'NO_SOURCE'})`);
                            console.error('Ready State:', video.readyState, `(${video.readyState === 0 ? 'HAVE_NOTHING' : video.readyState === 1 ? 'HAVE_METADATA' : video.readyState === 2 ? 'HAVE_CURRENT_DATA' : video.readyState === 3 ? 'HAVE_FUTURE_DATA' : 'HAVE_ENOUGH_DATA'})`);
                            console.error('Full Error Object:', error);
                            console.error('============================');
                            
                            // Check network tab for more details
                            console.warn('💡 TIP: Check browser DevTools Network tab for detailed request/response information');
                            console.warn('💡 Look for the video file request - check its status code (404 = not found, 403 = forbidden, etc.)');
                            if (isS3) {
                              console.warn('💡 S3 CORS errors: Check S3 bucket CORS configuration');
                              console.warn('💡 S3 bucket needs CORS policy allowing your domain');
                              console.warn('💡 Required CORS headers: Access-Control-Allow-Origin, Access-Control-Allow-Methods');
                            }
                            console.warn('💡 CORS errors typically show: "CORS policy" or "Access-Control-Allow-Origin" in console');
                            console.warn('💡 Network errors typically show: Failed to load resource, timeout, or connection refused');
                            
                            setVideoError(true);
                            setVideoErrorDetails({
                              type: errorType,
                              message: errorMessage,
                              url: fullVideoUrl,
                            });
                          }}
                          onLoadStart={() => {
                            // Reset error state when video starts loading
                            setVideoError(false);
                            setVideoErrorDetails(null);
                            console.log('Video loading started...');
                          }}
                          onCanPlay={() => {
                            // Video loaded successfully
                            setVideoError(false);
                            setVideoErrorDetails(null);
                            console.log('Video can play - loaded successfully');
                          }}
                          onLoadedMetadata={() => {
                            // Metadata loaded successfully
                            setVideoError(false);
                            setVideoErrorDetails(null);
                            console.log('Video metadata loaded successfully');
                          }}
                          onStalled={() => {
                            console.warn('Video stalled - network may be slow or interrupted');
                          }}
                          onSuspend={() => {
                            console.warn('Video loading suspended - may indicate network issues');
                          }}
                          onTimeUpdate={() => {
                            // Track video progress every few seconds
                            const video = videoRef.current;
                            if (video && videoData?.video?.resource_id) {
                              const currentTime = video.currentTime;
                              const duration = video.duration;
                              
                              if (!duration || duration === 0) return;
                              
                              // Update progress every 10 seconds during normal playback
                              const completionRate = (currentTime / duration) * 100;
                              
                              // Update local state immediately for UI feedback (throttled to avoid too many updates)
                              if (videoProgress === null || Math.abs(videoProgress.completion_rate - completionRate) > 1) {
                                const isCompleted = completionRate >= 95;
                                setVideoProgress({
                                  completion_rate: completionRate,
                                  isCompleted: isCompleted
                                });
                              }
                              
                              // Debounce: Only update to server every 10 seconds
                              if (!progressUpdateIntervalRef.current) {
                                progressUpdateIntervalRef.current = setTimeout(() => {
                                  updateVideoProgress(videoData.video.resource_id, currentTime, duration);
                                  progressUpdateIntervalRef.current = null;
                                }, 10000);
                              }
                            }
                          }}
                          onEnded={() => {
                            // Mark video as completed immediately when it ends
                            if (videoData?.video?.resource_id && videoRef.current) {
                              // Clear any pending timeout to avoid duplicate updates
                              if (progressUpdateIntervalRef.current) {
                                clearTimeout(progressUpdateIntervalRef.current);
                                progressUpdateIntervalRef.current = null;
                              }
                              const duration = videoRef.current.duration;
                              updateVideoProgress(videoData.video.resource_id, duration, duration);
                              
                              // Find the next video in the list
                              if (videosList && videosList.length > 0 && videoData.video_id) {
                                const currentIndex = videosList.findIndex((v: any) => v.video_id === videoData.video_id);
                                if (currentIndex >= 0 && currentIndex < videosList.length - 1) {
                                  const nextVideo = videosList[currentIndex + 1];
                                  const nextVideoDurationSeconds = nextVideo.video?.duration || 0;
                                  const nextVideoDurationMinutes = Math.round(nextVideoDurationSeconds / 60);
                                  
                                  setNextVideoInfo({
                                    title: nextVideo.title || nextVideo.video?.name || `Video ${currentIndex + 2}`,
                                    duration: nextVideoDurationMinutes,
                                    videoId: nextVideo.video_id
                                  });
                                  setCountdown(5);
                                  setShowNextVideoOverlay(true);
                                  
                                  // Start countdown timer
                                  countdownIntervalRef.current = setInterval(() => {
                                    setCountdown((prev) => {
                                      if (prev <= 1) {
                                        // Auto-play next video when countdown reaches 0
                                        if (countdownIntervalRef.current) {
                                          clearInterval(countdownIntervalRef.current);
                                          countdownIntervalRef.current = null;
                                        }
                                        setShowNextVideoOverlay(false);
                                        loadVideo(nextVideo.video_id);
                                        return 0;
                                      }
                                      return prev - 1;
                                    });
                                  }, 1000);
                                }
                              }
                            }
                          }}
                          onPause={() => {
                            // Save progress immediately when video is paused
                            if (videoData?.video?.resource_id && videoRef.current) {
                              // Clear any pending timeout to avoid duplicate updates
                              if (progressUpdateIntervalRef.current) {
                                clearTimeout(progressUpdateIntervalRef.current);
                                progressUpdateIntervalRef.current = null;
                              }
                              const currentTime = videoRef.current.currentTime;
                              const duration = videoRef.current.duration;
                              updateVideoProgress(videoData.video.resource_id, currentTime, duration);
                            }
                          }}
                        >
                          <source 
                            src={getFullVideoUrl(videoData.video.url, videoData.video_id)} 
                            type={videoData.video.mime_type || 'video/mp4'}
                          />
                          Your browser does not support the video tag.
                        </video>
                        {showPlayButtonOverlay && (() => {
                          // Get thumbnail URL from videoData or videosList
                          let thumbnailUrl: string | null = null;
                          if (videoData?.thumbnail?.url) {
                            thumbnailUrl = videoData.thumbnail.url;
                          } else if (videoData?.thumbnail?.storage_path) {
                            thumbnailUrl = getFullThumbnailUrl(videoData.thumbnail.storage_path);
                          } else if (videoData?.video_id && videosList.length > 0) {
                            // Find thumbnail from videosList by matching video_id
                            const currentVideo = videosList.find((v: any) => v.video_id === videoData.video_id);
                            if (currentVideo?.thumbnail?.url) {
                              thumbnailUrl = currentVideo.thumbnail.url;
                            } else if (currentVideo?.thumbnail?.storage_path) {
                              thumbnailUrl = getFullThumbnailUrl(currentVideo.thumbnail.storage_path);
                            }
                          }
                          
                          const showBlackOverlay = !thumbnailUrl || thumbnailLoadError;
                          
                          return (
                            <div 
                              className="absolute inset-0 flex items-center justify-center cursor-pointer z-10"
                              style={{
                                backgroundImage: thumbnailUrl && !thumbnailLoadError ? `url(${thumbnailUrl})` : undefined,
                                backgroundColor: showBlackOverlay ? '#111827' : 'transparent',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat'
                              }}
                              onClick={() => {
                                if (videoRef.current) {
                                  setShowPlayButtonOverlay(false);
                                  videoRef.current.play();
                                }
                              }}
                            >
                              {/* Invisible image to test thumbnail loading */}
                              {thumbnailUrl && (
                                <img
                                  src={thumbnailUrl}
                                  alt=""
                                  style={{ display: 'none' }}
                                  onError={() => setThumbnailLoadError(true)}
                                  onLoad={() => setThumbnailLoadError(false)}
                                />
                              )}
                              {/* Black overlay fallback when thumbnail fails to load */}
                              {showBlackOverlay && (
                                <div className="absolute inset-0 bg-black"></div>
                              )}
                              <img 
                                src="/video_play_btn.svg" 
                                alt="Play Video" 
                                className="w-24 h-24 hover:scale-110 transition-transform duration-200 relative z-10"
                              />
                            </div>
                          );
                        })()}
                      </div>
                      )}
                      {/* Instructor Info Box - Bottom Left */}
                      {videoData && (videoData.trainer_name || videoData.trainer_title) && (
                        <div className="microskill-skill-lecture-info">
                          <div className="microskill-slide-trainer">
                            {videoData.trainer_name && (
                              <span className="microskill-slide-trainer-name text-start">
                                {videoData.trainer_name.split(' ')[0]}
                              </span>
                            )}
                            {videoData.trainer_title && (
                              <span className="microskill-slide-trainer-role text-start">
                                {videoData.trainer_title}
                              </span>
                            )}
                          </div>
                          {videoProgress && (
                            <div className="microskill-video-progress-info" style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {videoProgress.isCompleted ? (
                                <>
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="8" cy="8" r="7" stroke="#4CAF50" strokeWidth="2" fill="none"/>
                                    <path d="M5 8L7 10L11 6" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                  <span style={{ color: '#4CAF50', fontSize: '14px' }}>Video Completed</span>
                                </>
                              ) : (
                                <>
                                  <span style={{ fontSize: '14px', color: '#00D9FF' }}>
                                    Progress: {Math.round(videoProgress.completion_rate)}%
                                  </span>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Description Section
              <div className="microskill-description-section text-start">
                <h3 className="microskill-description-title">Description</h3>
                <p className="microskill-description-text">{microskill.description}</p>
              </div> */}
            </div>

            {/* Right Sidebar */}
            <div className="microskill-sidebar">
              {/* Course Progress Section */}
              <div className="microskill-sidebar-section">
                <div 
                  className="microskill-sidebar-header"
                  onClick={() => setCourseProgressExpanded(!courseProgressExpanded)}
                >
                  <h3 className="microskill-sidebar-title">{microskill.title}</h3>
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 16 16" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                    className={`microskill-chevron ${courseProgressExpanded ? 'expanded' : ''}`}
                  >
                    <path d="M4 6L8 10L12 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                {courseProgressExpanded && (
                  <div className="microskill-sidebar-content">
                    <div className="microskill-course-progress">
                      <span className="microskill-course-progress-label">Course Progress</span>
                      <span className="microskill-course-progress-value">
                        {adjustedCourseProgress.completed}/{adjustedCourseProgress.total}
                      </span>
                    </div>
                    <div className="microskill-course-progress-bar-wrapper">
                      <div className="microskill-course-progress-bar">
                        <div 
                          className="microskill-course-progress-bar-fill" 
                          style={{ width: `${(adjustedCourseProgress.completed / adjustedCourseProgress.total) * 100}%` }}
                        />
                      </div>
                    </div>
                    <p className="microskill-course-progress-text text-start">
                      {adjustedCourseProgress.remaining} more videos to unlock badge
                    </p>
                    
                    {/* Microlearning Modules List */}
                    <div className="microskill-modules-list">
                      {adjustedModules.map((module) => {
                        // Debug logging for completion status
                        if (module.id === 2) {
                          console.log(`Microlearning 2 completion check: videoId=${module.videoId}, completed=${module.completed}, title=${module.title}`);
                        }
                        return (
                        <div 
                          key={module.id} 
                          className={`microskill-module-item ${module.current ? 'current' : ''} ${module.completed ? 'completed' : ''}`}
                          onClick={() => {
                            // Load the selected video when module is clicked
                            if (module.videoId) {
                              loadVideo(module.videoId);
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          {module.completed ? (
                            <div className="microskill-module-checkmark">
                              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="10" cy="10" r="9" stroke="#4CAF50" strokeWidth="2" fill="none"/>
                                <path d="M6 10L9 13L14 7" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                          ) : (
                            <div className="microskill-module-radio"></div>
                          )}
                          <div className="microskill-module-content text-start">
                            <div className="microskill-module-header">
                              <div className="microskill-module-title text-start">Microlearning {module.id}</div>
                              <div className="microskill-module-duration text-start">{module.duration} Hours</div>
                            </div>
                            <div className="microskill-module-name text-start">{formatTitle(module.title)}</div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Next Microskill Section */}
              {microskill.nextMicroskill.id > 0 && (
                <>
                  <div className="text-start">
                    <h3 className="microskill-sidebar-title">Next Microskill</h3>
                  </div>
                  <div className="microskill-sidebar-section">
                    {nextMicroskillExpanded && (
                  <div className="microskill-sidebar-content">
                    <div className="microskill-next-card">
                      {/* Header Section with Python Logo */}
                      <div className="microskill-next-card-header-image">
                        <div className="microskill-next-card-duration-badge">{microskill.nextMicroskill.duration} Hours</div>
                        <div className="microskill-next-card-logo-container">
                          {microskill.nextMicroskill.imageUrl ? (
                            <img 
                              src={getFullImageUrl(microskill.nextMicroskill.imageUrl)} 
                              alt={microskill.nextMicroskill.title}
                              className="microskill-next-card-python-logo"
                            />
                          ) : null}
                        </div>
                      </div>
                      
                      {/* Content Section */}
                      <div className="microskill-next-card-content">
                        <h4 className="microskill-next-card-title">{microskill.nextMicroskill.title}</h4>
                        <div className="microskill-next-card-meta">
                          <div className="microskill-next-meta-item">
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="7" cy="5" r="2.5" stroke="white" strokeWidth="1.5" fill="none"/>
                              <path d="M2 11C2 9.5 4 8.5 7 8.5C10 8.5 12 9.5 12 11" stroke="white" strokeWidth="1.5" fill="none"/>
                            </svg>
                            <span>{microskill.nextMicroskill.instructors}</span>
                          </div>
                          <div className="microskill-next-meta-item">
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M7 2C9.5 2 11.5 4 11.5 6.5C11.5 9 9.5 11 7 11C4.5 11 2.5 9 2.5 6.5C2.5 4 4.5 2 7 2Z" stroke="white" strokeWidth="1.5" fill="none"/>
                              <path d="M4 11.5L3 12.5M10 11.5L11 12.5" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                            <span>{microskill.nextMicroskill.microlearningCount} Microlearning</span>
                          </div>
                        </div>
                        <div className="microskill-play-next-wrapper">
                          <button 
                            className="microskill-play-next-btn"
                            onClick={() => {
                              if (microskill.nextMicroskill.id) {
                                // Allow access if skillstree is completed, even without subscription
                                // Otherwise check if next microskill is not free and user doesn't have subscription
                                if (microskill.nextMicroskill.isFree === false && !hasSubscription && !isSkillstreeCompleted) {
                                  setShowUpgradeModal(true);
                                  return;
                                }
                                window.scrollTo(0, 0);
                                navigate(`/skillstree/${categoryId}/${skillstreeId}/${microskill.nextMicroskill.id}`);
                              }
                            }}
                            disabled={!microskill.nextMicroskill.id}
                          >
                            Play Next
                          </button>
                          <button 
                            className="microskill-play-next-chevron"
                            onClick={() => setSubmodulesExpanded(!submodulesExpanded)}
                            disabled={microskill.nextMicroskill.subModules.length === 0}
                          >
                            <svg 
                              width="16" 
                              height="16" 
                              viewBox="0 0 16 16" 
                              fill="none" 
                              xmlns="http://www.w3.org/2000/svg"
                              style={{ transform: submodulesExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                            >
                              <path d="M4 6L8 10L12 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </div>
                        {submodulesExpanded && microskill.nextMicroskill.subModules.length > 0 && (
                        <div className="microskill-next-submodules">
                          {microskill.nextMicroskill.subModules.map((subModule) => (
                            <div key={subModule.id} className="microskill-submodule-item">
                              <div className="microskill-submodule-thumbnail">
                                {subModule.thumbnail ? (
                                  <img 
                                    src={getFullThumbnailUrl(subModule.thumbnail)} 
                                    alt={subModule.title}
                                    className="microskill-submodule-thumbnail-img"
                                    onLoad={() => console.log('Thumbnail image loaded successfully:', getFullThumbnailUrl(subModule.thumbnail!))}
                                    onError={(e) => {
                                      console.error('Thumbnail image failed to load:', {
                                        thumbnail: subModule.thumbnail,
                                        url: getFullThumbnailUrl(subModule.thumbnail!),
                                        error: e
                                      });
                                    }}
                                  />
                                ) : (
                                  <div style={{ 
                                    width: '100%', 
                                    height: '100%', 
                                    background: '#333', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    color: '#fff',
                                    fontSize: '10px'
                                  }}>
                                    No thumbnail
                                  </div>
                                )}
                              </div>
                              <div className="microskill-submodule-content text-start">
                                <div className="microskill-submodule-title">{formatTitle(subModule.title)}</div>
                                <div className="microskill-submodule-info">
                                  <div className="microskill-submodule-trainer">
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <circle cx="6" cy="4" r="2" stroke="white" strokeWidth="1" fill="none"/>
                                      <path d="M2 9C2 7.5 3.5 6.5 6 6.5C8.5 6.5 10 7.5 10 9" stroke="white" strokeWidth="1" fill="none"/>
                                    </svg>
                                    <span>{subModule.trainer}</span>
                                  </div>
                                  <div className="microskill-submodule-views">
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M6 2C8 2 9.5 3.5 10 5.5C9.5 7.5 8 9 6 9C4 9 2.5 7.5 2 5.5C2.5 3.5 4 2 6 2Z" stroke="white" strokeWidth="1" fill="none"/>
                                      <path d="M3 5.5L3.5 6M9 5.5L8.5 6" stroke="white" strokeWidth="1" strokeLinecap="round"/>
                                    </svg>
                                    <span>{subModule.views} views</span>
                                  </div>
                                </div>
                              </div>
                              <button 
                                className="microskill-submodule-start-btn"
                                onClick={() => {
                                  if (microskill.nextMicroskill.id) {
                                    // Allow access if skillstree is completed, even without subscription
                                    // Otherwise check if next microskill is not free and user doesn't have subscription
                                    if (microskill.nextMicroskill.isFree === false && !hasSubscription && !isSkillstreeCompleted) {
                                      setShowUpgradeModal(true);
                                      return;
                                    }
                                    window.scrollTo(0, 0);
                                    navigate(`/skillstree/${categoryId}/${skillstreeId}/${microskill.nextMicroskill.id}`);
                                  }
                                }}
                                disabled={!microskill.nextMicroskill.id}
                              >
                                Start
                              </button>
                            </div>
                          ))}
                        </div>
                        )}
                      </div>
                    </div>
                  </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Skillstree Completion Modal */}
      {showSkillstreeCompletionModal && skillstreeData?.image_url && (
        <div className="skillstree-completion-modal-overlay" onClick={() => {
          setShowSkillstreeCompletionModal(false);
          // Show passport modal after skillstree completion modal closes
          if (skillstreeId) {
            const skillstreeIdNum = parseInt(skillstreeId, 10);
            const checkAndShowPassport = async () => {
              const hasShownInStorage = await hasShownPassportModalForSkillstree(skillstreeIdNum, user?.id);
              if (!hasShownPassportModal && !hasShownInStorage) {
                setTimeout(async () => {
                  setShowPassportModal(true);
                  setHasShownPassportModal(true);
                  await markPassportModalAsShown(skillstreeIdNum, user?.id);
                }, 300); // Small delay for smooth transition
              }
            };
            checkAndShowPassport();
          }
        }}>
          <div className="skillstree-completion-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="skillstree-completion-modal-badge-container">
              <div className="skillstree-completion-modal-badge-frame">
                <img 
                  src={getFullImageUrl(skillstreeData.image_url)} 
                  alt="Skillstree Badge" 
                  className="skillstree-completion-modal-badge"
                  onError={(e) => {
                    // Fallback if badge image fails to load
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              </div>
            </div>
            <h2 className="skillstree-completion-modal-levelup">Level Up!</h2>
            <p className="skillstree-completion-modal-message">
              Congratulations! You've completed the Learning Pathway and earned your Skillsture Digital Passport.
            </p>
            <div className="skillstree-completion-modal-buttons">
              <button 
                className="skillstree-completion-modal-close-btn"
                onClick={() => {
                  setShowSkillstreeCompletionModal(false);
                  // Show passport modal after skillstree completion modal closes
                  if (skillstreeId) {
                    const skillstreeIdNum = parseInt(skillstreeId, 10);
                    const checkAndShowPassport = async () => {
                      const hasShownInStorage = await hasShownPassportModalForSkillstree(skillstreeIdNum, user?.id);
                      if (!hasShownPassportModal && !hasShownInStorage) {
                        setTimeout(async () => {
                          setShowPassportModal(true);
                          setHasShownPassportModal(true);
                          await markPassportModalAsShown(skillstreeIdNum, user?.id);
                        }, 300); // Small delay for smooth transition
                      }
                    };
                    checkAndShowPassport();
                  }
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Passport Modal */}
      {showPassportModal && skillstreeData?.image_url && (
        <div className="passport-modal-overlay" onClick={() => setShowPassportModal(false)}>
          <div className="passport-modal-content" onClick={(e) => e.stopPropagation()}>
            <img 
              src="/Passport.svg" 
              alt="Passport Badge" 
              className="passport-modal-badge"
            />
            <h2 className="passport-modal-levelup">Pathway Completed!</h2>
            <p className="passport-modal-congratulations">Congratulations {user?.first_name || ''}!</p>
            <p className="passport-modal-message">
              You've successfully completed the learning pathways:{' '}
              <span className="passport-modal-pathway-name-inline">
                <span className="passport-modal-checkmark-inline">
                  <img 
                    src="/green_check_mark.svg" 
                    alt="Checkmark" 
                    width="20" 
                    height="20"
                  />
                </span>
                {skillstreeData?.skillstree_name || 'Learning Pathway'}
              </span>
            </p>
            {passportNames.length > 0 && (
              <div className="passport-modal-passports-section">
                <p className="passport-modal-passports-label">Your Passports:</p>
                <div className="passport-modal-passports-list">
                  {passportNames.map((name, index) => (
                    <div key={index} className="passport-modal-passport-item">
                      <span className="passport-modal-passport-name">{name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="passport-modal-buttons">
              <button 
                className="passport-modal-view-passport-btn"
                onClick={() => {
                  setShowPassportModal(false);
                  navigate('/achievements');
                }}
              >
                View My Passport
                <span className="passport-modal-btn-arrow">→</span>
              </button>
              <button 
                className="passport-modal-explore-btn"
                onClick={() => {
                  setShowPassportModal(false);
                  navigate('/skillstree');
                }}
              >
                Explore New Pathway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Achievement Unlock Modal */}
      {showAchievementModal && microskill && (
        <div className="achievement-modal-overlay" onClick={() => setShowAchievementModal(false)}>
          <div className="achievement-modal-content" onClick={(e) => e.stopPropagation()}>
            {microskill.imageUrl && (
              <div className="achievement-modal-badge-container">
                <div className="achievement-modal-badge-inner">
                  <img 
                    src={getFullImageUrl(microskill.imageUrl)} 
                    alt="Achievement Badge" 
                    className="achievement-modal-badge"
                    onError={(e) => {
                      // Fallback if badge image fails to load
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                </div>
              </div>
            )}
            <h2 className="achievement-modal-title">Achievement Unlocked</h2>
            <p className="achievement-modal-message">
              Outstanding! You've mastered this skill. Ready for the next challenge?
            </p>
            <div className="achievement-modal-buttons">
              {microskill.nextMicroskill.id ? (
                <button 
                  className="achievement-modal-btn-primary"
                  onClick={() => {
                    setShowAchievementModal(false);
                    // Continue learning - navigate to next microskill (same as Play Next button)
                    // Allow access if skillstree is completed, even without subscription
                    // Otherwise check if next microskill is not free and user doesn't have subscription
                    if (microskill.nextMicroskill.isFree === false && !hasSubscription && !isSkillstreeCompleted) {
                      setShowUpgradeModal(true);
                      return;
                    }
                    window.scrollTo(0, 0);
                    navigate(`/skillstree/${categoryId}/${skillstreeId}/${microskill.nextMicroskill.id}`);
                  }}
                >
                  Continue Learning →
                </button>
              ) : null}
              <button 
                className={microskill.nextMicroskill.id ? "achievement-modal-btn-secondary" : "achievement-modal-btn-primary"}
                onClick={() => {
                  setShowAchievementModal(false);
                  navigate('/dashboard');
                }}
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade to SKILLSTURE Plus Modal */}
      {showUpgradeModal && (
        <div className="upgrade-modal-overlay" onClick={() => setShowUpgradeModal(false)}>
          <div className="upgrade-modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="upgrade-modal-close-btn"
              onClick={() => setShowUpgradeModal(false)}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="upgrade-modal-icon">
              <div className="upgrade-modal-padlock">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 10V8C6 5.79086 7.79086 4 10 4H14C16.2091 4 18 5.79086 18 8V10M6 10H4C3.44772 10 3 10.4477 3 11V19C3 19.5523 3.44772 20 4 20H20C20.5523 20 21 19.5523 21 19V11C21 10.4477 20.5523 10 20 10H18M6 10H18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            <h2 className="upgrade-modal-title">Upgrade to SKILLSTURE Plus</h2>
            <p className="upgrade-modal-description">
              You've reached the limit of your current plan. Upgrade to SKILLSTURE Plus to keep learning, explore every Skillstree path, and unlock all Microskills and Microlearning content.
            </p>
            <div className="upgrade-modal-features">
              <div className="upgrade-modal-feature-item">
                <div className="upgrade-modal-feature-check">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="9" stroke="#F96004" strokeWidth="2" fill="none"/>
                    <path d="M6 10L9 13L14 7" stroke="#F96004" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <span>Unlimited access to all Microskills</span>
              </div>
              <div className="upgrade-modal-feature-item">
                <div className="upgrade-modal-feature-check">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="9" stroke="#F96004" strokeWidth="2" fill="none"/>
                    <path d="M6 10L9 13L14 7" stroke="#F96004" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <span>Complete entire Skillstree pathways</span>
              </div>
              <div className="upgrade-modal-feature-item">
                <div className="upgrade-modal-feature-check">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="9" stroke="#F96004" strokeWidth="2" fill="none"/>
                    <path d="M6 10L9 13L14 7" stroke="#F96004" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <span>Earn all digital badges and digital passport</span>
              </div>
            </div>
            <div className="upgrade-modal-buttons">
              <button 
                className="upgrade-modal-btn-primary"
                onClick={() => {
                  setShowUpgradeModal(false);
                  navigate('/profile?tab=subscription');
                }}
              >
                Upgrade SKILLSTURE
              </button>
              <button 
                className="upgrade-modal-btn-secondary"
                onClick={() => setShowUpgradeModal(false)}
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MicroskillDetailPage;

